import { useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "@/lib/toast";
import api from "@/lib/api";

/**
 * "✨ Generate SEO" button used at the top of the SEO section on the brand
 * and clothing-item admin edit forms. Calls `POST /admin/seo/generate` with
 * a context payload built by the parent form, and writes the resulting
 * `seo_title` / `seo_description` back via the `onResult` callback.
 *
 *   <SeoGenerateButton
 *     buildPayload={() => ({ kind: "brand", name: form.name, ... })}
 *     onResult={({ seo_title, seo_description }) => setForm(f => ({
 *       ...f, seo_title, seo_description
 *     }))}
 *   />
 */
export function SeoGenerateButton({ buildPayload, onResult, disabled }) {
  const [loading, setLoading] = useState(false);
  const click = async () => {
    const payload = buildPayload?.() || {};
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await api.post("/admin/seo/generate", payload);
      onResult?.(data);
      toast.success("SEO copy generated");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not generate SEO copy.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };
  return (
    <button
      type="button"
      onClick={click}
      disabled={loading || disabled}
      className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 border border-forest text-forest hover:bg-forest hover:text-ivory transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      data-testid="seo-generate-button"
    >
      {loading ? (
        <>
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating…
        </>
      ) : (
        <>
          <Sparkles className="w-3.5 h-3.5" /> Generate SEO
        </>
      )}
    </button>
  );
}
